import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Download, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, API, getToken, formatApiError } from "@/lib/api";
import { inr } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const err = (e) => { if (e?.message !== "cancelled") toast.error(formatApiError(e?.response?.data?.detail)); };
const KV = ({ k, v, strong }) => <div className="flex justify-between text-sm"><span className="text-muted-foreground">{k}</span><span className={strong ? "font-bold text-emerald-500" : "tabular"}>{v}</span></div>;

function PanFix({ fid, onSaved }) {
  const { can } = useAuth();
  const [pan, setPan] = useState("");
  const save = () => api.put(`/franchise/${fid}`, { pan }).then(() => { toast.success("PAN saved"); onSaved(); }).catch(err);
  return (
    <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-2 text-xs" data-testid="fp-pan-missing">
      <p className="text-amber-600">No PAN on file — the higher no-PAN TDS rate will apply.</p>
      {can("fr.edit") && <div className="mt-2 flex gap-2"><Input className="h-8" value={pan} maxLength={10} onChange={(e) => setPan(e.target.value.toUpperCase())} placeholder="ABCDE1234F" data-testid="fp-pan-input" />
        <Button size="sm" className="h-8" onClick={save} disabled={pan.length !== 10} data-testid="fp-pan-save">Save PAN</Button></div>}
    </div>
  );
}

function NewPayout({ open, onClose, onSaved }) {
  const [frs, setFrs] = useState([]);
  const [f, setF] = useState({ franchise_id: "", period_from: "", period_to: "" });
  const [pv, setPv] = useState(null);
  useEffect(() => { if (open) { api.get("/franchise").then(({ data }) => setFrs(data)).catch(() => {}); setPv(null); } }, [open]);
  useEffect(() => {
    if (!f.franchise_id || !f.period_from || !f.period_to) return;
    api.post("/franchise-payouts/preview", f).then(({ data }) => setPv(data)).catch(err);
  }, [f]);
  const create = () => api.post("/franchise-payouts", f).then(() => { toast.success("Franchise payout created"); onSaved(); onClose(); }).catch(err);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card sm:max-w-lg" data-testid="new-fr-payout-dialog">
        <DialogHeader><DialogTitle className="font-head">New Franchise Commission Payout</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Franchise Partner</Label>
            <select className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={f.franchise_id} onChange={(e) => setF({ ...f, franchise_id: e.target.value })} data-testid="fp-franchise">
              <option value="">Select…</option>{frs.map((x) => <option key={x.id} value={x.id}>{x.name} ({x.referral_number})</option>)}</select></div>
          <div><Label>Period From</Label><Input type="date" value={f.period_from} onChange={(e) => setF({ ...f, period_from: e.target.value })} data-testid="fp-from" /></div>
          <div><Label>Period To</Label><Input type="date" value={f.period_to} onChange={(e) => setF({ ...f, period_to: e.target.value })} data-testid="fp-to" /></div>
        </div>
        {pv && <PayoutSummary p={{ franchise_name: pv.franchise.name, referral_number: pv.franchise.referral_number, period_from: pv.period_from, period_to: pv.period_to,
          total_orders: pv.total_orders, gross_commission: pv.gross_commission, tds_estimate: pv.tds }} />}
        {pv && !pv.franchise.pan && <PanFix fid={pv.franchise.id} onSaved={() => setF({ ...f })} />}
        {pv?.overlap && <p className="text-sm text-rose-500">Overlaps existing payout {pv.overlap}.</p>}
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={create} disabled={!pv || pv.overlap || !pv.gross_commission} data-testid="fp-create">Create Payout</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const PayoutSummary = ({ p }) => {
  const t = p.tds_calc || p.tds_estimate || {};
  return (
    <div className="rounded-lg border border-border bg-secondary/40 p-3" data-testid="fr-payout-summary">
      <KV k="Franchise Partner" v={p.franchise_name} /><KV k="Referral Number" v={p.referral_number} />
      <KV k="Commission Period" v={`${p.period_from} → ${p.period_to}`} /><KV k="Total Completed Orders" v={p.total_orders} />
      <KV k="Gross Commission" v={inr(p.gross_commission)} />
      <KV k="Applicable TDS Rate" v={t.applied ? `${t.rate}% (Sec ${t.section})` : "Not applied"} />
      {t.catch_up > 0 && <KV k="FY catch-up (earlier untaxed)" v={inr(t.catch_up)} />}
      <KV k="TDS Amount" v={`− ${inr(t.tds_amount || 0)}`} />
      <KV k="Final Net Payable" v={inr(t.net ?? p.gross_commission)} strong />
      {!t.applied && t.reason && <p className="mt-1 text-[11px] text-muted-foreground">{t.reason}</p>}
    </div>
  );
};

function PayDialog({ p, onClose, onSaved }) {
  const [f, setF] = useState({});
  const [live, setLive] = useState(null);
  useEffect(() => {
    if (!p) return;
    setF({ utr_number: "", payment_method: "Bank Transfer", payment_date: new Date().toISOString().slice(0, 10) });
    api.post("/franchise-payouts/preview", { franchise_id: p.franchise_id, period_from: p.period_from, period_to: p.period_to })
      .then(({ data }) => setLive(data.tds)).catch(() => setLive(null));
  }, [p]);
  if (!p) return null;
  const net = (live || p.tds_estimate).net;
  const pay = () => api.post(`/franchise-payouts/${p.payout_id}/pay`, f).then(() => { toast.success("Payout paid · statement generated"); onSaved(); onClose(); }).catch(err);
  return (
    <Dialog open={!!p} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card sm:max-w-lg" data-testid="fr-pay-dialog">
        <DialogHeader><DialogTitle className="font-head">Approve & Pay · {p.payout_id}</DialogTitle></DialogHeader>
        <PayoutSummary p={{ ...p, tds_estimate: live || p.tds_estimate }} />
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Payment Method</Label><select className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={f.payment_method} onChange={(e) => setF({ ...f, payment_method: e.target.value })} data-testid="fp-method">
            {["Bank Transfer", "UPI", "Cheque"].map((m) => <option key={m}>{m}</option>)}</select></div>
          <div><Label>Payment Date</Label><Input type="date" value={f.payment_date || ""} onChange={(e) => setF({ ...f, payment_date: e.target.value })} data-testid="fp-date" /></div>
          <div className="col-span-2"><Label>UTR / Transaction ID *</Label><Input value={f.utr_number || ""} onChange={(e) => setF({ ...f, utr_number: e.target.value })} data-testid="fp-utr" /></div>
        </div>
        <p className="text-[11px] text-muted-foreground">RazorpayX is not connected — pay the net amount manually, then confirm here.</p>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={pay} disabled={!f.utr_number} data-testid="fp-pay">Approve & Pay {inr(net)}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GstDialog({ p, onClose, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => { if (p) setF({ supplier_name: p.franchise_name, supplier_gstin: "", recipient_gstin: "", invoice_number: "", invoice_date: new Date().toISOString().slice(0, 10),
    place_of_supply: "", sac_code: "996211", taxable_value: p.gross_commission, cgst: 0, sgst: 0, igst: 0, ...(p.gst_invoice || {}) }); }, [p]);
  if (!p) return null;
  const save = () => api.post(`/franchise-payouts/${p.payout_id}/gst-invoice`, f).then(() => { toast.success("GST invoice recorded"); onSaved(); onClose(); }).catch(err);
  const F = ([k, l, t = "text"]) => <div key={k}><Label>{l}</Label><Input type={t} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`gst-${k}`} /></div>;
  return (
    <Dialog open={!!p} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-card sm:max-w-lg" data-testid="gst-dialog">
        <DialogHeader><DialogTitle className="font-head">Franchise GST Invoice · {p.payout_id}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Record the tax invoice issued BY the franchise (supplier) to Connect Tym. Use CGST+SGST for intra-state or IGST for inter-state supply.</p>
        <div className="grid grid-cols-2 gap-3">{[["supplier_name", "Supplier Name"], ["supplier_gstin", "Supplier GSTIN *"], ["recipient_gstin", "Connect Tym GSTIN"],
          ["invoice_number", "Invoice Number *"], ["invoice_date", "Invoice Date", "date"], ["place_of_supply", "Place of Supply"], ["sac_code", "SAC Code"],
          ["taxable_value", "Taxable Value", "number"], ["cgst", "CGST", "number"], ["sgst", "SGST", "number"], ["igst", "IGST", "number"]].map(F)}</div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="gst-save">Save GST Invoice</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const printStatement = (p) => {
  const w = window.open(`${API}/franchise-payouts/${p.payout_id}/statement?fmt=pdf&token=${getToken()}`, "_blank");
  if (!w) toast.error("Allow pop-ups to print");
};

export function FranchisePayouts() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [pay, setPay] = useState(null);
  const [gst, setGst] = useState(null);
  const load = useCallback(() => api.get("/franchise-payouts").then(({ data }) => setRows(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const dl = (p, fmt) => window.open(`${API}/franchise-payouts/${p.payout_id}/statement?fmt=${fmt}&token=${getToken()}`, "_blank");
  const reject = (p) => { const reason = window.prompt("Reason for rejecting this payout?"); if (reason) api.post(`/franchise-payouts/${p.payout_id}/reject`, { reason }).then(load).catch(err); };
  return (
    <div data-testid="fr-payouts">
      {can("fr.payout_create") && <Button className="mb-3" onClick={() => setNewOpen(true)} data-testid="new-fr-payout-btn"><Plus className="mr-1 h-4 w-4" /> New Franchise Payout</Button>}
      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">{["Payout", "Franchise", "Period", "Orders", "Gross", "TDS", "Net", "Status", "Statement", "Actions"].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground">No franchise payouts yet</TableCell></TableRow>}
            {rows.map((p) => (
              <TableRow key={p.id} data-testid={`fr-payout-row-${p.payout_id}`}>
                <TableCell className="font-mono text-xs">{p.payout_id}{p.statement_number && <p className="text-[10px] text-muted-foreground">{p.statement_number}</p>}</TableCell>
                <TableCell><p className="font-medium">{p.franchise_name}</p><p className="text-[11px] text-muted-foreground">{p.referral_number}</p></TableCell>
                <TableCell className="text-xs">{p.period_from} → {p.period_to}</TableCell>
                <TableCell className="tabular">{p.total_orders}</TableCell>
                <TableCell className="tabular">{inr(p.gross_commission)}</TableCell>
                <TableCell className="tabular text-rose-500">{inr(p.tds_amount ?? p.tds_estimate?.tds_amount ?? 0)}</TableCell>
                <TableCell className="tabular font-semibold text-emerald-500">{inr(p.net_payable ?? p.tds_estimate?.net)}</TableCell>
                <TableCell className="text-xs font-semibold">{p.status}</TableCell>
                <TableCell><div className="flex gap-1">
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => dl(p, "pdf")} data-testid={`stmt-pdf-${p.payout_id}`}><Download className="mr-1 h-3 w-3" />PDF</Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => dl(p, "excel")} data-testid={`stmt-excel-${p.payout_id}`}>Excel</Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => printStatement(p)} data-testid={`stmt-print-${p.payout_id}`}><Printer className="h-3 w-3" /></Button>
                </div></TableCell>
                <TableCell><div className="flex gap-1">
                  {p.status === "Pending Approval" && can("fr.payout_pay") && <>
                    <Button size="sm" className="h-7 bg-emerald-600 px-2 text-xs hover:bg-emerald-700" onClick={() => setPay(p)} data-testid={`fr-pay-${p.payout_id}`}>Approve & Pay</Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-rose-500" onClick={() => reject(p)} data-testid={`fr-reject-${p.payout_id}`}>Reject</Button></>}
                  {p.status !== "Rejected" && (can("fr.payout_create") || can("fr.payout_pay")) &&
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setGst(p)} data-testid={`fr-gst-${p.payout_id}`}>{p.gst_invoice ? "GST ✓" : "GST Invoice"}</Button>}
                </div></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <NewPayout open={newOpen} onClose={() => setNewOpen(false)} onSaved={load} />
      <PayDialog p={pay} onClose={() => setPay(null)} onSaved={load} />
      <GstDialog p={gst} onClose={() => setGst(null)} onSaved={load} />
    </div>
  );
}
